import React, { useState, useEffect, useRef, useContext } from 'react';
import { Search, Mic, Code, ArrowForward, KeyboardCommandKey } from '@mui/icons-material';
import { AssistantCommandContext } from './AssistantCommandContext';

const QUICK_PROMPTS = [
  { id: 'auth', label: 'Comment authentifier mon application ?', icon: Code },
  { id: 'payment', label: 'Comment créer un paiement ?', icon: Code },
  { id: 'webhook', label: 'Comment configurer les webhooks ?', icon: Code },
  { id: 'errors', label: 'Quels sont les codes d\'erreur courants ?', icon: Code },
];

export default function AssistantCommandPalette({ isOpen, onClose }) {
  const commandRef = useContext(AssistantCommandContext);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);

  const filtered = query.trim()
    ? QUICK_PROMPTS.filter((p) =>
        p.label.toLowerCase().includes(query.toLowerCase())
      )
    : QUICK_PROMPTS;

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelected(0);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((s) => (s < filtered.length - 1 ? s + 1 : s));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((s) => (s > 0 ? s - 1 : 0));
      }
      if (e.key === 'Enter' && filtered[selected]) {
        e.preventDefault();
        const label = filtered[selected].label;
        commandRef?.current?.sendPrompt?.(label);
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, filtered, selected, commandRef, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[90] assistant-command-backdrop animate-fade-in"
        onClick={onClose}
        role="presentation"
      />
      <div className="fixed inset-0 z-[91] flex items-start justify-center pt-[15vh] px-4 pointer-events-none">
        <div
          className="w-full max-w-xl bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-slate-700/60 shadow-2xl overflow-hidden pointer-events-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Commandes rapides"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-700/60">
            <Search className="text-slate-500" sx={{ fontSize: 20 }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher une action ou une question..."
              className="flex-1 bg-transparent text-slate-100 placeholder-slate-500 text-[15px] outline-none"
            />
            <kbd className="hidden sm:inline-flex items-center px-2 py-1 rounded bg-slate-800 text-slate-500 text-xs">
              Esc
            </kbd>
          </div>

          <div className="max-h-80 overflow-y-auto py-2">
            <div className="px-2 py-1.5">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-2 mb-1">
                Suggestions
              </p>
              {filtered.length === 0 ? (
                <div className="px-4 py-8 text-center text-slate-500 text-sm">
                  Aucun résultat pour « {query} »
                </div>
              ) : (
                filtered.map((item, i) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      commandRef?.current?.sendPrompt?.(item.label);
                      onClose();
                    }}
                    onMouseEnter={() => setSelected(i)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 transition-all duration-150 ${
                      i === selected
                        ? 'bg-amber-400/10 text-amber-300 border border-amber-400/25'
                        : 'text-slate-300 hover:bg-slate-800/60 border border-transparent'
                    }`}
                  >
                    <item.icon className="text-slate-500" sx={{ fontSize: 18 }} />
                    <span className="flex-1 text-sm font-medium">{item.label}</span>
                    <ArrowForward sx={{ fontSize: 16 }} className="text-slate-500" />
                  </button>
                ))
              )}
            </div>

            <div className="border-t border-slate-700/60 mt-2 pt-2 px-2">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-2 mb-1">
                Actions
              </p>
              <button
                type="button"
                onClick={() => {
                  commandRef?.current?.switchToVoice?.();
                  onClose();
                }}
                className="w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-3 text-slate-300 hover:bg-amber-400/10 hover:text-amber-300 transition-colors"
              >
                <Mic sx={{ fontSize: 18 }} className="text-slate-500" />
                <span className="text-sm font-medium">Passer au mode vocal</span>
              </button>
            </div>
          </div>

          <div className="px-4 py-2 border-t border-slate-700/60 flex items-center justify-between text-xs text-slate-500">
            <span>↑↓ Naviguer · ↵ Sélectionner</span>
            <span className="flex items-center gap-1">
              <KeyboardCommandKey sx={{ fontSize: 14 }} />K pour fermer
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
